import React from 'react';

import { observer } from 'mobx-react';

import { CircularProgress } from '@mui/material';

import { Button } from 'components/Button';

import NavigationBlocker from 'components/NavigationBlocker';

import API from 'utils/api';
import { getErrorMessage } from 'utils/api/errors';

import { useStore } from 'utils/hooks/store';

import { MembershipMode } from 'utils/api/modules/memberGroups';

import { emptyRule } from '../components/MemberGroupForm';
import { RuleBuilder, conditionHasValue, RuleDraft } from '../components/RuleBuilder';
import { RULE_MAX_CONDITIONS, serializeRule } from '../components/RuleBuilder/registry';
import { RulePreview } from '../components/RulePreview';
import { FieldError } from '../components/RuleBuilder/styles';
import ConvertToDynamicModal from './ConvertToDynamicModal';
import { RuleEditActions, RuleEditSection, Section, SectionBody, SectionHeader, SectionTitle } from './styles';

const ConvertToDynamic: React.FunctionComponent<{ onExit: () => void }> = observer(({ onExit }) => {
  const { memberGroupDetails, notification } = useStore();
  const { group } = memberGroupDetails;
  const [rule, setRule] = React.useState<RuleDraft>(emptyRule);
  const initialRule = React.useRef(rule).current;
  const [submitted, setSubmitted] = React.useState(false);
  const [leavingCount, setLeavingCount] = React.useState<number | null>(null);
  const [busy, setBusy] = React.useState(false);

  if (!group) {
    return null;
  }

  const dirty = JSON.stringify(rule) !== JSON.stringify(initialRule);
  const ruleValid =
    rule.conditions.length > 0 &&
    rule.conditions.length <= RULE_MAX_CONDITIONS &&
    rule.conditions.every(conditionHasValue);

  const convert = async (): Promise<void> => {
    setBusy(true);
    const result = await memberGroupDetails.switchMembershipMode(MembershipMode.Dynamic, rule);
    setBusy(false);
    if (!result.ok) {
      notification.enqueueErrorSnackbar(getErrorMessage(result.error, 'Could not change membership'));
      return;
    }
    notification.enqueueSuccessSnackbar('Switched to dynamic. Membership now updates automatically.');
    setLeavingCount(null);
    onExit();
  };

  const onSave = async (): Promise<void> => {
    setSubmitted(true);
    if (!ruleValid) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await API.memberGroups.previewCounts({ rule: serializeRule(rule), group: group.uuid })();
      if (data.leaving > 0) {
        setBusy(false);
        setLeavingCount(data.leaving);
        return;
      }
    } catch {
      setBusy(false);
      notification.enqueueErrorSnackbar('Couldn’t check who this affects. Try again.');
      return;
    }
    await convert();
  };

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Change to dynamic membership</SectionTitle>
      </SectionHeader>
      <SectionBody>
        <NavigationBlocker
          when={dirty}
          shouldBlockNavigation={() => dirty}
          navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
        />
        <RuleEditSection>
          <div>
            <RuleBuilder rule={rule} onChange={setRule} submitted={submitted} />
            {submitted && !ruleValid && <FieldError>Give every condition a value before saving.</FieldError>}
          </div>
          <RulePreview rule={rule} groupUuid={group.uuid} />
        </RuleEditSection>
        <RuleEditActions>
          <Button variant="outlined" size="large" type="button" onClick={onExit}>
            Cancel
          </Button>
          <Button variant="contained" size="large" type="button" onClick={onSave} disabled={busy}>
            {busy ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </RuleEditActions>
      </SectionBody>

      <ConvertToDynamicModal
        open={leavingCount !== null}
        leavingCount={leavingCount ?? 0}
        saving={busy}
        onClose={() => setLeavingCount(null)}
        onConfirm={convert}
      />
    </Section>
  );
});

export default ConvertToDynamic;
